export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

function redirectToSignin() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/signin') return;
  const next = window.location.pathname + window.location.search;
  window.location.assign(`/signin?next=${encodeURIComponent(next)}`);
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  opts: { skipAuthRedirect?: boolean } = {},
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { code?: string; message?: string; details?: unknown }
      | null;
    const code = body?.code ?? 'UNKNOWN';
    // 세션 만료·미인증 응답이면 로그인 페이지로 이동
    if (!opts.skipAuthRedirect && (res.status === 401 || code === 'AUTH_REQUIRED' || code === 'NOT_ALLOWLISTED')) {
      redirectToSignin();
    }
    throw {
      code,
      message: body?.message ?? `요청 실패 (status=${res.status})`,
      details: body?.details,
    } as ApiError;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
};

export interface SessionUser {
  id: string;
  email: string;
  name?: string | null;
  avatarUrl?: string | null;
}

export async function fetchSession(): Promise<{ user: SessionUser | null }> {
  try {
    return await request<{ user: SessionUser | null }>('/auth/me', {}, { skipAuthRedirect: true });
  } catch {
    return { user: null };
  }
}
